'use client';

import type { NavigationProps } from '@routeroyal/theme-core';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';
import { Menu, Search, User, X } from 'lucide-react';
import Link from 'next/link';
import { memo, useCallback, useEffect, useState } from 'react';
import { AdaptiveLogo } from './AdaptiveLogo';
import { StoneInvestmentCurrencySwitcher } from './CurrencySwitcher';
import { StoneInvestmentLocaleSwitcher } from './LocaleSwitcher';
import { StoneInvestmentMenuOverlay } from './MenuOverlay';
import { StoneSearchOverlay } from './StoneSearchOverlay';

/**
 * Stone Investment Navigation Component
 * 
 * Concept One Design:
 * - Scroll-aware transparency with backdrop blur
 * - Intelligent background detection for logo/text color
 * - Centered logo with "Investment" subtitle
 * - Menu trigger on left, Search + Language on right
 * - Full-screen menu overlay on menu click
 * - Auth-aware account action via useCustomerAuth
 * - Hierarchy links support for multi-tenant navigation
 * 
 * Color Logic:
 * - Menu open: Always white (dark overlay background)
 * - Scrolled: Always dark (light scrolled nav background)
 * - Not scrolled: Detects if over dark hero, otherwise defaults to dark
 * - Not scrolled + light background: dark (safe default)
 */
export const StoneInvestmentNavigation = memo(function StoneInvestmentNavigation({ 
  logo, 
  links: _links, 
  ctaText, 
  ctaLink, 
  locale = 'en',
  messages,
  hierarchyLinks, 
}: NavigationProps) {
  const stoneMessages = (messages?.stone || {}) as Record<string, Record<string, string>>;
  const navMessages = stoneMessages.navigation || {};
  const accessMessages = stoneMessages.accessibility || {};
  
  const t = (key: string, fallback: string): string => {
    return navMessages[key] || fallback;
  };

  let customer: { id?: string } | null = null;
  try {
    const auth = useCustomerAuth();
    customer = auth.customer;
  } catch {
    // CustomerAuthProvider may not be available in all contexts
  }

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isOverDarkHero, setIsOverDarkHero] = useState(false);

  const detectDarkHero = useCallback(() => {
    // Fast path: explicit data attribute (set by StoneCinematicHeroCarousel and other dark heroes)
    const explicitDark = document.querySelector('[data-dark-hero="true"]');
    if (explicitDark) {
      const rect = explicitDark.getBoundingClientRect();
      // Nav sits at top ~80px; hero must overlap that region
      if (rect.top <= 80 && rect.bottom > 80) {
        return true;
      }
    }

    // Check for video elements (always dark)
    const video = document.querySelector('video, .hero-video');
    if (video) {
      const rect = video.getBoundingClientRect();
      if (rect.top <= 80 && rect.bottom > 80) {
        return true;
      }
    }

    // Fallback: check elements with "hero" in class or tag header with dark bg
    const candidates = document.querySelectorAll('header, [class*="hero"]');
    for (const element of candidates) {
      const rect = element.getBoundingClientRect();
      if (rect.top <= 80 && rect.bottom > 80) {
        const style = window.getComputedStyle(element);
        const bg = style.backgroundColor;
        if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
          const match = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          if (match) {
            const [, rStr, gStr, bStr] = match;
            const r = Number(rStr);
            const g = Number(gStr);
            const b = Number(bStr);
            if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
              const brightness = (r * 299 + g * 587 + b * 114) / 1000;
              if (brightness < 128) {
                return true;
              }
            }
          }
        }
      }
    }

    return false;
  }, []);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const isScrolled = window.scrollY > 50;
          setScrolled(isScrolled);
          
          if (!isScrolled) {
            setIsOverDarkHero(detectDarkHero());
          }
          
          ticking = false;
        });
        ticking = true;
      }
    };

    // Run detection immediately and again after a short delay
    // (hero images may not be in the DOM on first paint)
    setIsOverDarkHero(detectDarkHero());
    const retryTimer = setTimeout(() => setIsOverDarkHero(detectDarkHero()), 150);

    // Watch for hero elements being added to the DOM
    const observer = new MutationObserver(() => {
      if (!scrolled) setIsOverDarkHero(detectDarkHero());
    });
    observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      clearTimeout(retryTimer);
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [detectDarkHero, scrolled]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Cmd+K / Ctrl+K global shortcut to open search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(prev => {
          if (!prev) setIsMenuOpen(false);
          return !prev;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const useLightMode = isMenuOpen || (!scrolled && isOverDarkHero);
  const textColorClass = useLightMode ? 'text-white' : 'text-stone-900';

  const accountHref = customer?.id
    ? `/${locale}/account`
    : (ctaLink || `/${locale}/login`);
  const accountLabel = customer?.id
    ? t('myAccount', 'My Account')
    : (ctaText || t('signIn', 'Sign In'));
  const enquireHref = `/${locale}/contact`;
  const enquireLabel = t('enquire', 'Enquire');

  return (
    <>
      {/* Hierarchy breadcrumb bar */}
      {hierarchyLinks?.parent && (
        <div className="fixed top-0 left-0 w-full z-51 bg-stone-900 text-white">
          <div className="container mx-auto px-6 md:px-12 py-1.5 flex items-center gap-2 text-xs tracking-wide"
            style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <Link href={hierarchyLinks.parent.href} className="hover:text-stone-300 transition-colors">
              {hierarchyLinks.parent.label}
            </Link>
            {hierarchyLinks.children && hierarchyLinks.children.length > 0 && (
              <>
                <span className="text-stone-500">/</span>
                {hierarchyLinks.children.map((child, i) => (
                  <span key={child.href} className="flex items-center gap-2">
                    {i > 0 && <span className="text-stone-500">·</span>}
                    <Link href={child.href} className="hover:text-stone-300 transition-colors">
                      {child.label}
                    </Link>
                  </span>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      <nav 
        className={`fixed left-0 w-full z-50 transition-all duration-700 ${
          hierarchyLinks?.parent ? 'top-8' : 'top-0'
        } ${
          isMenuOpen ? 'pointer-events-none' : ''
        } ${
          scrolled && !isMenuOpen 
            ? 'bg-stone-50/95 backdrop-blur-md py-4 border-b border-stone-200' 
            : isOverDarkHero && !isMenuOpen
              ? 'bg-linear-to-b from-black/50 via-black/25 to-transparent py-8'
              : 'bg-transparent py-8'
        }`}
        role="navigation" 
        aria-label={accessMessages.mainNavigation || 'Main navigation'}
      >
        <div className="container mx-auto px-4 sm:px-6 md:px-12 grid grid-cols-[auto_1fr_auto] items-center">
          {/* Left: Menu Trigger
              On mobile we widen the tap target via py-2 -m-2 so a finger
              never misses, and touch-manipulation removes the 300ms iOS
              tap delay that made the menu feel unresponsive. */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`group flex items-center gap-2 sm:gap-3 transition-colors duration-300 -m-2 p-2 touch-manipulation pointer-events-auto ${textColorClass}`}
            aria-label={isMenuOpen ? t('closeMenu', 'Close Menu') : t('openMenu', 'Open Menu')}
            aria-expanded={isMenuOpen}
            aria-controls="stone-mobile-menu"
          >
            <div className="relative w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center">
              {isMenuOpen ? <X size={22} className="sm:w-6 sm:h-6" /> : <Menu size={22} className="sm:w-6 sm:h-6" />}
            </div>
            <span className="hidden md:block uppercase text-xs tracking-ultra font-sans font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {t('menu', 'Menu')}
            </span>
          </button>

          {/* Center: Logo
              CRITICAL: When the menu overlay is open we drop pointer-events
              and fade the logo. The menu overlay sits at a lower z-index, so
              without this a tap that lands above the first menu item would
              actually hit the logo and navigate to the home page — which is
              the root cause of "tapping The Collection takes me to Route
              Royal" on phones. */}
          <div
            className={`flex justify-center min-w-0 transition-opacity duration-300 ${
              isMenuOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}
            aria-hidden={isMenuOpen}
          >
            <Link 
              href={`/${locale}`}
              className="text-center transition-colors duration-300"
              tabIndex={isMenuOpen ? -1 : 0}
            >
              <AdaptiveLogo
                logoUrl={logo}
                fallbackText="Stone Investment"
                colorMode={useLightMode ? 'light' : 'dark'}
                height={scrolled ? 40 : 52}
              />
            </Link>
          </div>

          {/* Right: Actions
              Same pattern as the logo — the search button and locale/account
              controls are visually disabled while the menu is open so an
              imprecise tap can't ever land on them. */}
          <div
            className={`flex items-center gap-3 sm:gap-4 md:gap-6 transition-all duration-300 ${textColorClass} ${
              isMenuOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}
            aria-hidden={isMenuOpen}
          >
            <Link
              href={enquireHref}
              onClick={() => analyticsEvents.custom('cta_click', {
                cta_text: enquireLabel,
                cta_location: 'nav',
                cta_destination: enquireHref,
                cta_variant: 'enquire',
              })}
              className="text-xs uppercase tracking-[0.15em] font-sans hover:opacity-70 transition-opacity duration-300"
              aria-label={enquireLabel}
              tabIndex={isMenuOpen ? -1 : 0}
            >
              {enquireLabel}
            </Link>
            <div className="hidden md:flex items-center gap-4">
              <StoneInvestmentCurrencySwitcher />
              <StoneInvestmentLocaleSwitcher locale={locale} />
            </div>
            <Link
              href={accountHref}
              className="hidden md:flex items-center gap-2 hover:opacity-70 transition-opacity duration-300"
              aria-label={accountLabel}
              tabIndex={isMenuOpen ? -1 : 0}
            >
              <User size={18} strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-[0.15em] font-sans">
                {accountLabel}
              </span>
            </Link>
            <button 
              onClick={() => { setIsSearchOpen(true); setIsMenuOpen(false); }}
              className="hover:scale-110 transition-transform duration-300 -m-2 p-2 touch-manipulation"
              aria-label={t('search', 'Search')}
              tabIndex={isMenuOpen ? -1 : 0}
            >
              <Search size={20} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </nav>

      {/* Full Screen Menu Overlay */}
      <StoneInvestmentMenuOverlay 
        isOpen={isMenuOpen} 
        onClose={() => setIsMenuOpen(false)}
        locale={locale}
        messages={messages ?? undefined}
      />

      {/* Full Screen Search Overlay */}
      <StoneSearchOverlay
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        locale={locale}
        messages={messages ?? undefined}
      />
    </>
  );
});
